import React, { useRef, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useParams, useNavigate } from 'react-router-dom';
import { Canvas, useFrame } from '@react-three/fiber';
import { PerspectiveCamera, Stars } from '@react-three/drei';
import * as THREE from 'three';
import productsData from './products.json';

// ============================================
// PENTERACT COMPONENT (keeping your original)
// ============================================
function Penteract({ rotationX, rotationY, rotationZ, rotation4D, rotation5D, ambientSpeed }) {
  const linesRef = useRef();
  const [scrollY, setScrollY] = useState(0);
  const accumulatedRotationRef = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const currentScroll = window.scrollY;
      setScrollY(currentScroll / maxScroll);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const project5Dto4D = (x, y, z, w, v, distance = 3) => {
    const factor = distance / (distance - v);
    return [x * factor, y * factor, z * factor, w * factor];
  };

  const project4Dto3D = (x, y, z, w, distance = 4) => {
    const factor = distance / (distance - w);
    return [x * factor, y * factor, z * factor];
  };

  const rotate5DPlane = (vertices, angle, plane) => {
    return vertices.map(([x, y, z, w, v]) => {
      let [x1, y1, z1, w1, v1] = [x, y, z, w, v];
      switch(plane) {
        case 'xy': x1 = x * Math.cos(angle) - y * Math.sin(angle); y1 = x * Math.sin(angle) + y * Math.cos(angle); break;
        case 'xz': x1 = x * Math.cos(angle) - z * Math.sin(angle); z1 = x * Math.sin(angle) + z * Math.cos(angle); break;
        case 'xw': x1 = x * Math.cos(angle) - w * Math.sin(angle); w1 = x * Math.sin(angle) + w * Math.cos(angle); break;
        case 'xv': x1 = x * Math.cos(angle) - v * Math.sin(angle); v1 = x * Math.sin(angle) + v * Math.cos(angle); break;
        case 'yz': y1 = y * Math.cos(angle) - z * Math.sin(angle); z1 = y * Math.sin(angle) + z * Math.cos(angle); break;
        case 'yw': y1 = y * Math.cos(angle) - w * Math.sin(angle); w1 = y * Math.sin(angle) + w * Math.cos(angle); break;
        case 'yv': y1 = y * Math.cos(angle) - v * Math.sin(angle); v1 = y * Math.sin(angle) + v * Math.cos(angle); break;
        case 'zw': z1 = z * Math.cos(angle) - w * Math.sin(angle); w1 = z * Math.sin(angle) + w * Math.cos(angle); break;
        case 'zv': z1 = z * Math.cos(angle) - v * Math.sin(angle); v1 = z * Math.sin(angle) + v * Math.cos(angle); break;
        case 'wv': w1 = w * Math.cos(angle) - v * Math.sin(angle); v1 = w * Math.sin(angle) + v * Math.cos(angle); break;
        default: break;
      }
      return [x1, y1, z1, w1, v1];
    });
  };

  useFrame((state, delta) => {
    if (!linesRef.current) return;
    accumulatedRotationRef.current += ambientSpeed * delta;
    const ambientRotation = accumulatedRotationRef.current;

    const size = 0.9;
    const vertices5D = [];
    for (let a = 0; a < 2; a++) {
      for (let b = 0; b < 2; b++) {
        for (let c = 0; c < 2; c++) {
          for (let d = 0; d < 2; d++) {
            for (let e = 0; e < 2; e++) {
              vertices5D.push([a ? size : -size, b ? size : -size, c ? size : -size, d ? size : -size, e ? size : -size]);
            }
          }
        }
      }
    }

    const scrollAngle = scrollY * Math.PI * 3;
    let rotated = vertices5D;
    rotated = rotate5DPlane(rotated, ambientRotation * 0.3, 'xy');
    rotated = rotate5DPlane(rotated, ambientRotation * 0.4, 'xz');
    if (rotationX !== 0 || ambientSpeed !== 0) {
      rotated = rotate5DPlane(rotated, rotationX * 0.01 + ambientRotation * 0.25, 'yz');
      rotated = rotate5DPlane(rotated, rotationX * 0.008 + ambientRotation * 0.2, 'yw');
      rotated = rotate5DPlane(rotated, rotationX * 0.006 + ambientRotation * 0.15, 'yv');
    }
    if (rotationY !== 0 || ambientSpeed !== 0) {
      rotated = rotate5DPlane(rotated, rotationY * 0.01 + ambientRotation * 0.28, 'xz');
      rotated = rotate5DPlane(rotated, rotationY * 0.008 + ambientRotation * 0.22, 'xw');
      rotated = rotate5DPlane(rotated, rotationY * 0.006 + ambientRotation * 0.18, 'xv');
    }
    if (rotationZ !== 0 || ambientSpeed !== 0) {
      rotated = rotate5DPlane(rotated, rotationZ * 0.01 + scrollAngle * 0.3 + ambientRotation * 0.32, 'xy');
      rotated = rotate5DPlane(rotated, rotationZ * 0.008 + ambientRotation * 0.24, 'xw');
      rotated = rotate5DPlane(rotated, rotationZ * 0.006 + ambientRotation * 0.19, 'zv');
    }
    if (rotation4D !== 0 || ambientSpeed !== 0) {
      rotated = rotate5DPlane(rotated, rotation4D * 0.01 + ambientRotation * 0.35, 'xw');
      rotated = rotate5DPlane(rotated, rotation4D * 0.009 + ambientRotation * 0.27, 'yw');
      rotated = rotate5DPlane(rotated, rotation4D * 0.008 + ambientRotation * 0.21, 'zw');
    }
    if (rotation5D !== 0 || ambientSpeed !== 0) {
      rotated = rotate5DPlane(rotated, rotation5D * 0.012 + ambientRotation * 0.38, 'xv');
      rotated = rotate5DPlane(rotated, rotation5D * 0.01 + ambientRotation * 0.29, 'yv');
      rotated = rotate5DPlane(rotated, rotation5D * 0.009 + ambientRotation * 0.23, 'zv');
      rotated = rotate5DPlane(rotated, rotation5D * 0.011 + ambientRotation * 0.31, 'wv');
    }

    const vertices4D = rotated.map(v => project5Dto4D(...v));
    const vertices3D = vertices4D.map(v => project4Dto3D(...v));

    const edges = [];
    for (let i = 0; i < 32; i++) {
      for (let dim = 0; dim < 5; dim++) {
        const j = i ^ (1 << dim);
        if (j > i) edges.push([i, j]);
      }
    }

    const positions = [];
    const colors = [];
    edges.forEach(([i, j]) => {
      positions.push(...vertices3D[i], ...vertices3D[j]);
      const depth1 = rotated[i][4];
      const depth2 = rotated[j][4];
      const hue1 = (depth1 + size) / (size * 2);
      const hue2 = (depth2 + size) / (size * 2);
      const color1 = hue1 > 0.5 ? [0, 1, 1] : hue1 > 0 ? [1, 0, 1] : [1, 1, 0];
      const color2 = hue2 > 0.5 ? [0, 1, 1] : hue2 > 0 ? [1, 0, 1] : [1, 1, 0];
      colors.push(...color1, ...color2);
    });

    linesRef.current.geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    linesRef.current.geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    linesRef.current.geometry.attributes.position.needsUpdate = true;
    linesRef.current.geometry.attributes.color.needsUpdate = true;
    linesRef.current.position.y = Math.sin(state.clock.elapsedTime * 0.3) * 0.2;
  });

  return (
    <lineSegments ref={linesRef}>
      <bufferGeometry />
      <lineBasicMaterial vertexColors linewidth={2} transparent opacity={0.85} />
    </lineSegments>
  );
}

function StarField({ scrollY }) {
  const groupRef = useRef();
  useFrame(() => {
    if (groupRef.current) {
      const loopRange = 250;
      groupRef.current.position.z = -(scrollY * 90) % loopRange - loopRange / 2;
    }
  });
  return (
    <group ref={groupRef}>
      <Stars radius={220} depth={350} count={8000} factor={4} saturation={0} fade speed={0.5} />
    </group>
  );
}

function PenteractGlow({ rotationX, rotationY, rotationZ, rotation4D, rotation5D, ambientSpeed }) {
  return (
    <>
      <Penteract rotationX={rotationX} rotationY={rotationY} rotationZ={rotationZ} rotation4D={rotation4D} rotation5D={rotation5D} ambientSpeed={ambientSpeed} />
      <pointLight position={[0, 0, 0]} intensity={1.5} color="#00ffff" distance={12} decay={2} />
      <pointLight position={[2, 2, 2]} intensity={0.8} color="#ff00ff" distance={10} decay={2} />
    </>
  );
}

// ============================================
// NAVBAR COMPONENT (shrinks on scroll)
// ============================================
function Navbar() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      padding: scrolled ? '0.8rem 2rem' : '1.5rem 2rem',
      background: scrolled ? 'rgba(0, 0, 0, 0.95)' : 'transparent',
      backdropFilter: scrolled ? 'blur(10px)' : 'none',
      borderBottom: scrolled ? '1px solid rgba(0, 255, 255, 0.2)' : 'none',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      transition: 'all 0.3s ease',
      fontFamily: 'Orbitron, monospace'
    }}>
      <Link to="/" style={{
        fontSize: scrolled ? '1rem' : '1.3rem',
        fontWeight: 900,
        color: '#00ffff',
        textDecoration: 'none',
        letterSpacing: '0.1em',
        textShadow: '0 0 10px rgba(0, 255, 255, 0.5)',
        transition: 'all 0.3s ease'
      }}>
        TECHNOLOGY RESIDUE
      </Link>
      <div style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
        <a href="#services" style={navLinkStyle}>Services</a>
        <a href="#products" style={navLinkStyle}>Products</a>
        <a href="#sustainability" style={navLinkStyle}>Sustainability</a>
        <a href="#contact" style={{
          ...navLinkStyle,
          background: 'rgba(0, 255, 255, 0.1)',
          border: '1px solid #00ffff',
          padding: '0.5rem 1rem'
        }}>Contact</a>
      </div>
    </nav>
  );
}

const navLinkStyle = {
  color: '#fff',
  textDecoration: 'none',
  fontSize: '0.85rem',
  letterSpacing: '0.1em',
  transition: 'color 0.3s ease',
  fontFamily: 'Rajdhani, sans-serif'
};

// ============================================
// PRODUCT CARD WITH 3D HOVER EFFECT
// ============================================
function ProductCard({ product, index }) {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef();
  const navigate = useNavigate();

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x: y * 20, y: -x * 20 });
  };

  const handleMouseLeave = () => {
    setTilt({ x: 0, y: 0 });
    setIsHovered(false);
  };

  return (
    <div
      ref={cardRef}
      onClick={() => navigate(`/product/${product.id}`)}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      style={{
        position: 'relative',
        width: '300px',
        height: '380px',
        cursor: 'pointer',
        perspective: '1000px',
        animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
      }}
    >
      <div style={{
        width: '100%',
        height: '100%',
        background: 'linear-gradient(135deg, rgba(0, 255, 255, 0.1) 0%, rgba(255, 0, 255, 0.05) 100%)',
        border: '1px solid rgba(0, 255, 255, 0.3)',
        borderRadius: '8px',
        overflow: 'hidden',
        transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) scale(${isHovered ? 1.05 : 1})`,
        transition: isHovered ? 'transform 0.1s ease-out' : 'transform 0.5s ease-out',
        boxShadow: isHovered 
          ? '0 25px 50px rgba(0, 255, 255, 0.3), 0 0 30px rgba(255, 0, 255, 0.2)' 
          : '0 10px 30px rgba(0, 0, 0, 0.5)'
      }}>
        {/* Product Image Placeholder */}
        <div style={{
          width: '100%',
          height: '200px',
          background: 'linear-gradient(45deg, #111 25%, #1a1a1a 50%, #111 75%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid rgba(0, 255, 255, 0.2)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            inset: 0,
            background: `radial-gradient(circle at ${50 + tilt.y * 2}% ${50 + tilt.x * 2}%, rgba(0, 255, 255, 0.2) 0%, transparent 50%)`,
            transition: 'background 0.1s ease'
          }} />
          <span style={{
            fontFamily: 'Orbitron, monospace',
            fontSize: '3rem',
            color: 'rgba(0, 255, 255, 0.3)'
          }}>◇</span>
        </div>

        {/* Product Info */}
        <div style={{ padding: '1.5rem' }}>
          <div style={{
            fontFamily: 'Orbitron, monospace',
            fontSize: '0.7rem',
            color: '#ff00ff',
            letterSpacing: '0.15em',
            marginBottom: '0.5rem'
          }}>
            {product.category}
          </div>
          <h3 style={{
            fontFamily: 'Orbitron, monospace',
            fontSize: '1.1rem',
            color: '#fff',
            margin: '0 0 0.5rem 0',
            letterSpacing: '0.05em'
          }}>
            {product.name}
          </h3>
          <div style={{
            fontFamily: 'Rajdhani, sans-serif',
            fontSize: '0.85rem',
            color: 'rgba(255, 255, 255, 0.6)',
            marginBottom: '1rem'
          }}>
            {product.material} • {product.recycledPercent}% Recycled
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontFamily: 'Orbitron, monospace',
            fontSize: '0.7rem',
            color: '#00ffff'
          }}>
            <span style={{
              background: 'rgba(0, 255, 255, 0.1)',
              border: '1px solid rgba(0, 255, 255, 0.3)',
              padding: '0.3rem 0.6rem',
              borderRadius: '4px'
            }}>
              ♻️ {product.co2Saved} CO₂ saved
            </span>
          </div>
        </div>

        {/* Hover Glow Effect */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: 'linear-gradient(90deg, transparent, #00ffff, #ff00ff, transparent)',
          opacity: isHovered ? 1 : 0,
          transition: 'opacity 0.3s ease'
        }} />
      </div>
    </div>
  );
}

// ============================================
// PRODUCT PASSPORT PAGE
// ============================================
function ProductPage() {
  const { id } = useParams();
  const product = productsData.products.find(p => p.id === id);

  if (!product) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontFamily: 'Orbitron, monospace'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ color: '#ff00ff' }}>Product Not Found</h1>
          <Link to="/" style={{ color: '#00ffff' }}>← Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #000 0%, #0a0a0a 50%, #000 100%)',
      color: '#fff',
      fontFamily: 'Rajdhani, sans-serif',
      padding: '2rem'
    }}>
      <style>{globalStyles}</style>
      
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        paddingTop: '4rem'
      }}>
        <Link to="/" style={{
          color: '#00ffff',
          textDecoration: 'none',
          fontFamily: 'Orbitron, monospace',
          fontSize: '0.85rem',
          letterSpacing: '0.1em',
          display: 'inline-block',
          marginBottom: '2rem'
        }}>
          ← BACK TO PRODUCTS
        </Link>

        {/* Product Header */}
        <div style={{
          background: 'rgba(0, 255, 255, 0.05)',
          border: '1px solid rgba(0, 255, 255, 0.2)',
          borderRadius: '8px',
          padding: '2rem',
          marginBottom: '2rem'
        }}>
          <div style={{
            fontFamily: 'Orbitron, monospace',
            fontSize: '0.75rem',
            color: '#ff00ff',
            letterSpacing: '0.2em',
            marginBottom: '0.5rem'
          }}>
            PRODUCT PASSPORT
          </div>
          <h1 style={{
            fontFamily: 'Orbitron, monospace',
            fontSize: '2.5rem',
            margin: '0 0 0.5rem 0',
            color: '#00ffff',
            textShadow: '0 0 20px rgba(0, 255, 255, 0.5)'
          }}>
            {product.name}
          </h1>
          <div style={{
            fontFamily: 'Orbitron, monospace',
            fontSize: '1rem',
            color: 'rgba(255, 255, 255, 0.5)',
            letterSpacing: '0.1em'
          }}>
            ID: {product.id}
          </div>
        </div>

        {/* Stats Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          <StatCard label="Material" value={product.material} />
          <StatCard label="Recycled Content" value={`${product.recycledPercent}%`} highlight />
          <StatCard label="Source" value={product.source} />
          <StatCard label="Weight" value={product.weight} />
          <StatCard label="CO₂ Saved" value={product.co2Saved} highlight />
          <StatCard label="Manufactured" value={product.manufactureDate} />
        </div>

        {/* Description */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '8px',
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <h3 style={{
            fontFamily: 'Orbitron, monospace',
            fontSize: '0.85rem',
            color: '#00ffff',
            letterSpacing: '0.1em',
            marginBottom: '1rem'
          }}>
            DESCRIPTION
          </h3>
          <p style={{
            fontSize: '1.1rem',
            lineHeight: 1.8,
            color: 'rgba(255, 255, 255, 0.8)',
            margin: 0
          }}>
            {product.description}
          </p>
        </div>

        {/* Recycle Section */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(0, 255, 0, 0.1) 0%, rgba(0, 255, 255, 0.05) 100%)',
          border: '1px solid rgba(0, 255, 0, 0.3)',
          borderRadius: '8px',
          padding: '2rem',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '3rem',
            marginBottom: '1rem'
          }}>♻️</div>
          <h3 style={{
            fontFamily: 'Orbitron, monospace',
            fontSize: '1.2rem',
            color: '#00ff00',
            letterSpacing: '0.1em',
            marginBottom: '1rem'
          }}>
            RECYCLE THIS PRODUCT
          </h3>
          <p style={{
            fontSize: '1rem',
            color: 'rgba(255, 255, 255, 0.7)',
            marginBottom: '1.5rem',
            maxWidth: '500px',
            margin: '0 auto 1.5rem'
          }}>
            When you're done with this product, send it back to us for re-recycling. 
            We'll turn it into something new!
          </p>
          <div style={{
            background: 'rgba(0, 0, 0, 0.3)',
            padding: '1rem',
            borderRadius: '4px',
            fontFamily: 'monospace',
            fontSize: '0.95rem',
            color: '#fff'
          }}>
            {product.returnAddress}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, highlight }) {
  return (
    <div style={{
      background: highlight ? 'rgba(0, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.02)',
      border: `1px solid ${highlight ? 'rgba(0, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)'}`,
      borderRadius: '8px',
      padding: '1.2rem'
    }}>
      <div style={{
        fontFamily: 'Orbitron, monospace',
        fontSize: '0.65rem',
        color: 'rgba(255, 255, 255, 0.5)',
        letterSpacing: '0.15em',
        marginBottom: '0.5rem'
      }}>
        {label.toUpperCase()}
      </div>
      <div style={{
        fontFamily: 'Orbitron, monospace',
        fontSize: '1.3rem',
        color: highlight ? '#00ffff' : '#fff'
      }}>
        {value}
      </div>
    </div>
  );
}

// ============================================
// MAIN HOMEPAGE
// ============================================
function HomePage() {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [rotationX] = useState(0);
  const [rotationY] = useState(0);
  const [rotationZ] = useState(0);
  const [rotation4D] = useState(0);
  const [rotation5D] = useState(0);
  const [ambientSpeed] = useState(0.15);

  useEffect(() => {
    const handleScroll = () => {
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      setScrollPosition(window.scrollY / maxScroll);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <style>{globalStyles}</style>
      <Navbar />

      {/* Fixed 3D Canvas */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100vh',
        zIndex: 1
      }}>
        <Canvas>
          <PerspectiveCamera makeDefault position={[0, 0, 8]} />
          <ambientLight intensity={0.2} />
          <pointLight position={[10, 10, 10]} intensity={0.8} color="#00ffff" />
          <pointLight position={[-10, -10, -10]} intensity={0.5} color="#ff00ff" />
          <StarField scrollY={scrollPosition} />
          <PenteractGlow
            rotationX={rotationX}
            rotationY={rotationY}
            rotationZ={rotationZ}
            rotation4D={rotation4D}
            rotation5D={rotation5D}
            ambientSpeed={ambientSpeed}
          />
        </Canvas>
      </div>

      {/* Overlays */}
      <div className="scanline" />
      <div className="vignette" />

      {/* Scrollable Content */}
      <div style={{ position: 'relative', zIndex: 2 }}>
        
        {/* Hero Section */}
        <section style={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          padding: '2rem'
        }}>
          <h1 className="section-title text-glow glitch" style={{
            fontSize: 'clamp(2.5rem, 8vw, 5rem)',
            color: '#00ffff',
            margin: 0,
            lineHeight: 1.1
          }}>
            TECHNOLOGY<br/>RESIDUE
          </h1>
          <p style={{
            fontFamily: 'Rajdhani, sans-serif',
            fontSize: 'clamp(1rem, 3vw, 1.5rem)',
            color: '#ff00ff',
            marginTop: '1.5rem',
            letterSpacing: '0.1em'
          }}>
            Engineering the Future, Layer by Layer
          </p>
          <p style={{
            fontFamily: 'Rajdhani, sans-serif',
            fontSize: '1rem',
            color: 'rgba(255, 255, 255, 0.6)',
            marginTop: '1rem',
            maxWidth: '500px'
          }}>
            RF Engineering • Advanced Manufacturing • Sustainable Production
          </p>
          <div style={{
            display: 'flex',
            gap: '1rem',
            marginTop: '2.5rem',
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            <a href="#contact" className="cta-button primary">Get a Quote</a>
            <a href="#products" className="cta-button secondary">View Products</a>
          </div>
          <div style={{
            marginTop: '4rem',
            animation: 'bounce 2s infinite'
          }}>
            <span style={{ color: '#00ffff', fontSize: '1.5rem' }}>↓</span>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '4rem 2rem'
        }}>
          <h2 className="section-title" style={{
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            color: '#00ffff',
            marginBottom: '1rem'
          }}>
            SERVICES
          </h2>
          <p style={{
            fontFamily: 'Rajdhani, sans-serif',
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '1.1rem',
            marginBottom: '3rem',
            textAlign: 'center',
            maxWidth: '600px'
          }}>
            From concept to creation, we bring your ideas to life with precision engineering and sustainable manufacturing.
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem',
            maxWidth: '900px',
            width: '100%'
          }}>
            {productsData.services.map((service, i) => (
              <div key={service.id} className="service-card" style={{
                background: 'rgba(0, 255, 255, 0.05)',
                border: '1px solid rgba(0, 255, 255, 0.2)',
                borderRadius: '8px',
                padding: '2rem',
                animation: `fadeInUp 0.6s ease-out ${i * 0.2}s both`
              }}>
                <h3 style={{
                  fontFamily: 'Orbitron, monospace',
                  fontSize: '1.3rem',
                  color: '#00ffff',
                  marginBottom: '1rem'
                }}>
                  {service.title}
                </h3>
                <p style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  color: 'rgba(255, 255, 255, 0.7)',
                  lineHeight: 1.7,
                  marginBottom: '1.5rem'
                }}>
                  {service.description}
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {service.features.map(feature => (
                    <span key={feature} style={{
                      fontFamily: 'Orbitron, monospace',
                      fontSize: '0.7rem',
                      color: '#ff00ff',
                      background: 'rgba(255, 0, 255, 0.1)',
                      border: '1px solid rgba(255, 0, 255, 0.3)',
                      padding: '0.3rem 0.6rem',
                      borderRadius: '4px'
                    }}>
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Products Section */}
        <section id="products" style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '4rem 2rem'
        }}>
          <h2 className="section-title" style={{
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            color: '#ff00ff',
            marginBottom: '1rem'
          }}>
            PRODUCTS
          </h2>
          <p style={{
            fontFamily: 'Rajdhani, sans-serif',
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '1.1rem',
            marginBottom: '3rem',
            textAlign: 'center',
            maxWidth: '600px'
          }}>
            Sustainable products made from recycled materials. Each item includes a digital product passport.
          </p>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '2rem',
            maxWidth: '1400px'
          }}>
            {productsData.products.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </div>
        </section>

        {/* Sustainability Section */}
        <section id="sustainability" style={{
          minHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '4rem 2rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>♻️</div>
          <h2 className="section-title" style={{
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            color: '#00ff00',
            marginBottom: '1rem'
          }}>
            SUSTAINABLE BY DESIGN
          </h2>
          <p style={{
            fontFamily: 'Rajdhani, sans-serif',
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '1.2rem',
            maxWidth: '700px',
            lineHeight: 1.8,
            marginBottom: '2rem'
          }}>
            We built our own filament recycler to transform plastic waste into new products. 
            From ocean-recovered plastics to post-industrial waste, every material gets a second life.
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '1.5rem',
            maxWidth: '600px',
            width: '100%'
          }}>
            <div className="stat-box">
              <div style={{ fontSize: '2rem', color: '#00ffff' }}>90%+</div>
              <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>Recycled Content</div>
            </div>
            <div className="stat-box">
              <div style={{ fontSize: '2rem', color: '#00ffff' }}>0</div>
              <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>Waste to Landfill</div>
            </div>
            <div className="stat-box">
              <div style={{ fontSize: '2rem', color: '#00ffff' }}>∞</div>
              <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>Re-recyclable</div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" style={{
          minHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '4rem 2rem',
          textAlign: 'center'
        }}>
          <h2 className="section-title" style={{
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            color: '#00ffff',
            marginBottom: '1rem'
          }}>
            LET'S BUILD SOMETHING
          </h2>
          <p style={{
            fontFamily: 'Rajdhani, sans-serif',
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '1.1rem',
            marginBottom: '2rem',
            maxWidth: '500px'
          }}>
            Have a project in mind? Whether it's RF engineering, custom manufacturing, or sustainable product development — we're ready to help.
          </p>
          <a href="mailto:grantdh@techresidue.com" className="cta-button primary" style={{
            fontSize: '1.1rem',
            padding: '1rem 2.5rem'
          }}>
            grantdh@techresidue.com
          </a>
        </section>

        {/* Footer */}
        <footer style={{
          padding: '2rem',
          borderTop: '1px solid rgba(0, 255, 255, 0.2)',
          textAlign: 'center'
        }}>
          <p style={{
            fontFamily: 'Orbitron, monospace',
            fontSize: '0.8rem',
            color: 'rgba(255, 255, 255, 0.5)',
            letterSpacing: '0.1em'
          }}>
            © 2026 TECHNOLOGY RESIDUE • Albuquerque, NM
          </p>
        </footer>
      </div>
    </>
  );
}

// ============================================
// GLOBAL STYLES
// ============================================
const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&family=Rajdhani:wght@300;400;600&display=swap');

  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { overflow-x: hidden; background: #000; }
  html { scroll-behavior: smooth; }

  .scanline {
    position: fixed;
    top: 0; left: 0;
    width: 100%; height: 100%;
    background: repeating-linear-gradient(0deg, rgba(0, 255, 255, 0.02), rgba(0, 255, 255, 0.02) 1px, transparent 1px, transparent 2px);
    pointer-events: none;
    z-index: 10;
  }

  .vignette {
    position: fixed;
    top: 0; left: 0;
    width: 100%; height: 100%;
    background: radial-gradient(ellipse at center, transparent 0%, rgba(0, 0, 0, 0.7) 100%);
    pointer-events: none;
    z-index: 5;
  }

  .section-title {
    font-family: 'Orbitron', monospace;
    font-weight: 900;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  .text-glow {
    text-shadow: 0 0 10px rgba(0, 255, 255, 0.8), 0 0 20px rgba(0, 255, 255, 0.6), 0 0 30px rgba(0, 255, 255, 0.4);
  }

  .glitch {
    animation: glitch 3s infinite;
  }

  @keyframes glitch {
    0%, 100% { transform: translate(0); }
    33% { transform: translate(-2px, 2px); }
    66% { transform: translate(2px, -2px); }
  }

  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(10px); }
  }

  .cta-button {
    font-family: 'Orbitron', monospace;
    font-size: 0.9rem;
    letter-spacing: 0.1em;
    padding: 0.8rem 1.8rem;
    border-radius: 4px;
    text-decoration: none;
    transition: all 0.3s ease;
    cursor: pointer;
  }

  .cta-button.primary {
    background: rgba(0, 255, 255, 0.1);
    border: 2px solid #00ffff;
    color: #00ffff;
  }

  .cta-button.primary:hover {
    background: rgba(0, 255, 255, 0.2);
    box-shadow: 0 0 20px rgba(0, 255, 255, 0.5);
  }

  .cta-button.secondary {
    background: transparent;
    border: 2px solid rgba(255, 255, 255, 0.3);
    color: #fff;
  }

  .cta-button.secondary:hover {
    border-color: #ff00ff;
    color: #ff00ff;
  }

  .service-card:hover {
    border-color: rgba(0, 255, 255, 0.5);
    box-shadow: 0 0 30px rgba(0, 255, 255, 0.2);
  }

  .stat-box {
    background: rgba(0, 255, 255, 0.05);
    border: 1px solid rgba(0, 255, 255, 0.2);
    border-radius: 8px;
    padding: 1.5rem;
    font-family: 'Orbitron', monospace;
  }
`;

// ============================================
// APP WITH ROUTER
// ============================================
export default function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/product/:id" element={<ProductPage />} />
      </Routes>
    </Router>
  );
}